import ReactApexChart from 'react-apexcharts'
import { useParams } from 'react-router-dom'
import { useStatsSingleProject } from '../../../hooks/useStatsSingleProject'
import { EventType } from '../../../Types'
import { useState } from 'react'
import { logBase, toUS } from '../utils'
import { COLOR_GOLD_LIGHT, COLOR_SILVER_LIGHT } from '../../../constants'

export const FunnelCounts = () => {
  const { projectId } = useParams() as { projectId: string }
  const { countsPerState, facebookCampaignsInsights, expectedRandomizations } = useStatsSingleProject(projectId)

  const adImpressions = facebookCampaignsInsights.reduce((acc, campaign) => {
    return acc + Number(campaign.impressions)
  }, 0)

  const adReach = facebookCampaignsInsights.reduce((acc, campaign) => {
    return acc + Number(campaign.reach)
  }, 0)

  const adClicks = facebookCampaignsInsights.reduce((acc, campaign) => {
    return acc + Number(campaign.clicks)
  }, 0)

  const adClicksUnique = facebookCampaignsInsights.reduce((acc, campaign) => {
    return acc + Number(campaign.unique_clicks)
  }, 0)

  const labelValuePairs: [string, number][] = [
    ['Ad Views', adImpressions],
    ['Ad Reach', adReach],
    ['Ad Clicks', adClicks],
    ['Ad Clicks (unique)', adClicksUnique],
    ['Signed Up', countsPerState[EventType.SignedUp]],
    ['Pre-qualified Leads', countsPerState[EventType.AssignedToSite]],
    ['Qualified Leads', countsPerState[EventType.NewReferral]],
    ['Screened', countsPerState[EventType.SignedConsent]],
    ['Randomized', countsPerState[EventType.SubjectRandomised]],
    [
      'Forecasted Randomizations',
      expectedRandomizations.passesRandomization + countsPerState[EventType.SubjectRandomised],
    ],
  ]

  const separator = ' — '
  const height = 500

  const [hiddenLabels, setHiddenLabels] = useState<string[]>([])

  function getFillColor(label: string) {
    if (label.toLowerCase().includes('rando')) return COLOR_GOLD_LIGHT
    if (label.toLowerCase().includes('screen')) return COLOR_SILVER_LIGHT
    return '#96caff'
    //return '#adfb7f'
  }

  const visibleLabelValuePairs = labelValuePairs.filter(([label, value]) => !hiddenLabels.includes(label))

  // we dont actualy use the value as value, as the funnel would look weird
  // - for example: impressions can be 500.000, so 30 randomizations would not even show up on the graph - too big difference.
  const data = visibleLabelValuePairs.map(([label, value], idx) => {
    return { x: `${label}${separator}${toUS(value)}`, y: logBase(value + 1, 2), fillColor: getFillColor(label) }
  })

  const state = {
    series: [{ name: 'Funnel', data }],
    options: {
      chart: {
        type: 'bar',
        height: height,
        events: {
          dataPointSelection: function (event, chartContext, opts) {
            event.preventDefault()

            const isMiddleClick = event?.button === 1
            if (isMiddleClick) {
              opts.w.globals.selectedDataPoints = []
              const usedLabel = opts.w.globals.labels[opts.dataPointIndex]
              const label = usedLabel.split(separator)[0]
              setHiddenLabels((prev) => [...prev, label])
            }
          },
          click: undefined,
          markerClick: undefined,
        },
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          isFunnel: true,
        },
      },
      stroke: {
        width: 1,
        colors: ['#fff'],
      },
      title: {
        text: 'Funnel',
      },
      dataLabels: {
        enabled: true,
        textAnchor: 'middle',
        offsetY: 4,
        style: {
          colors: visibleLabelValuePairs.map(([label, count]) => {
            if (label.toLowerCase().includes('rando')) return '#fff'
            if (label.toLowerCase().includes('screen')) return '#fff'

            return '#00004d'
          }),
        },
        formatter: function (val, opt) {
          const usedLabel = opt.w.globals.labels[opt.dataPointIndex]
          const count = usedLabel.split(separator)[1]
          return count
        },
      },
      tooltip: {
        enabled: false,
        y: {
          formatter: (value, { series, seriesIndex, dataPointIndex, w }) => {
            return 'Click to remove block'
          },
        },
      },
    },
  }

  return (
    <div className="topzIndexDataLabels" style={{ height: '100%', position: 'relative' }}>
      {/** The label overlays */}
      <div
        style={{
          height: '100%',
          width: '100%',
          position: 'absolute',
          top: 0,
          paddingTop: 68,
          paddingBottom: 39,
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-around',
          pointerEvents: 'none',
        }}
      >
        {visibleLabelValuePairs.map(([label, count]) => {
          return (
            <p key={label + count} style={{ textAlign: 'center', fontSize: 10 }}>
              {label}
            </p>
          )
        })}
      </div>
      <div className="chart" style={{ height: '100%' }}>
        {/*@ts-ignore */}
        <ReactApexChart options={state.options} series={state.series} type="bar" height={height} />
      </div>
      <div id="html-dist-barchart"></div>
      <p className="position-absolute text-muted mt-2" style={{ bottom: -20, fontSize: '0.7rem' }}>
        Middle click mouse to remove funnel-step.
      </p>
    </div>
  )
}
